/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid util/clipboard.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import type { DataGridItem } from '../types'

// table-core resolves the selection to index rectangles and leaves the text to
// userland, "since the delimiter, the null representation, and any quoting
// rules are application decisions". This is that decision, made once for all
// four editions: TSV, because that is what spreadsheets paste as columns.

const DELIMITER = '\t'
const ROW_SEPARATOR = '\n'
// Disjoint regions are separated the way a spreadsheet shows a gap.
const REGION_SEPARATOR = '\n\n'

export interface ClipboardBounds {
  maxColumnIndex: number
  maxRowIndex: number
  minColumnIndex: number
  minRowIndex: number
}

export interface ClipboardRow {
  getValue: (columnId: string) => unknown
  original: DataGridItem
}

export interface ClipboardTable {
  getCellSelectionBounds: () => ClipboardBounds[]
  getRowModel: () => { rows: ClipboardRow[] }
}

// The grid's own visible columns, in display order — the same order the
// selection's column indexes address.
export interface ClipboardColumn {
  id: string
  formatter?: (value: unknown, item: DataGridItem) => string
}

// A tab or a newline inside a value would break the grid the paste target
// rebuilds, so a value carrying either is quoted the way TSV consumers expect.
const escape = (text: string): string =>
  (/[\t\n"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text)

export const serializeCellSelection = (table: ClipboardTable, columns: ClipboardColumn[]): string => {
  const { rows } = table.getRowModel()

  return table
    .getCellSelectionBounds()
    .map(bounds => {
      const lines: string[] = []
      for (let rowIndex = bounds.minRowIndex; rowIndex <= bounds.maxRowIndex; rowIndex++) {
        const row = rows[rowIndex]
        if (!row) {
          continue
        }

        const cells: string[] = []
        for (let columnIndex = bounds.minColumnIndex; columnIndex <= bounds.maxColumnIndex; columnIndex++) {
          const column = columns[columnIndex]
          if (!column) {
            continue
          }

          const value = row.getValue(column.id)
          // The formatter is what the cell displays; a copied date should
          // paste as the date the user is looking at, not its raw value.
          cells.push(escape(column.formatter ? column.formatter(value, row.original) : String(value ?? '')))
        }

        lines.push(cells.join(DELIMITER))
      }

      return lines.join(ROW_SEPARATOR)
    })
    .filter(region => region !== '')
    .join(REGION_SEPARATOR)
}

export const copyCellSelection = async (table: ClipboardTable, columns: ClipboardColumn[]): Promise<string> => {
  const text = serializeCellSelection(table, columns)
  if (text === '') {
    return text
  }

  try {
    await navigator.clipboard.writeText(text)
  } catch {
    // Denied permission, an insecure context, or a browser without the async
    // clipboard: fall back to the selection-and-execCommand route, which only
    // works inside the user gesture that asked for the copy — which is exactly
    // where this runs.
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('aria-hidden', 'true')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.append(textarea)
    textarea.select()
    try {
      document.execCommand('copy')
    } finally {
      textarea.remove()
    }
  }

  return text
}
